import { ScheduleModel } from '@maxpreps/apiserviceclients/apiModels';
import { publicOrigin } from '@maxpreps/network/src/origins';
import { ITeamContext } from '@maxpreps/shared/contexts/TeamContext';
import { push as routerPush } from '@maxpreps/shared/functions/primitiveHelpers/routerHelpers';
import { addGlobalQueryParams } from '@maxpreps/shared/functions/primitiveHelpers/urlHelpers';
import { AppType } from '@maxpreps/shared/functions/utils/requestContextUtils';
import rewrittenPages from '../server/pageMappings';
import { getTeamUrl } from './../utils/navigation';
import { Page } from './../utils/pages';

const {
  endsWith
} = require('@maxpreps/shared/functions/server/pageMappingUtils');

// https://stackoverflow.com/questions/14368596/how-can-i-check-that-two-objects-have-the-same-set-of-property-names
const compareKeys = (a, b) => {
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  return JSON.stringify(aKeys) === JSON.stringify(bKeys);
};

export interface ICSRAnchorAttributes {
  href: string;
  as?: string;
}

const getCSRRewriteAttributes = (
  href: string,
  schoolid: string,
  ssid: string,
  allSeasonId: string,
  appType: AppType,
  isAdmin?: boolean,
  scheduleModel?: ScheduleModel
): ICSRAnchorAttributes => {
  if (!href) return { href: '' };
  if (!schoolid || !ssid) {
    return { href };
  }

  // find the rewritten page.
  // If one exists, return the as
  const url = new URL(href, publicOrigin);

  if (isAdmin) url.searchParams.set('admin', '1');
  const asHref = url.href;

  const rewrittenPage = rewrittenPages.find(r => r.test(url));
  const isStandardSport =
    (scheduleModel ?? ScheduleModel.Standard) === ScheduleModel.Standard;
  const isNonStandardSportWFPage =
    endsWith(Page.Schedule, url) || endsWith(Page.Stats, url);

  // non-standard sports need to SSR to WF
  if (rewrittenPage && (isStandardSport || !isNonStandardSportWFPage)) {
    const { searchParams } = url;
    searchParams.set('apptype', `${appType}`);

    let page: any = null;
    // At time of writing there is a known hole.
    // if the query param object expects values to match too then this logic
    // will not cover that...
    if (Array.isArray(rewrittenPage.react)) {
      page = rewrittenPage.react.find(
        p => !p.queryParams || compareKeys(p.queryParams, searchParams)
      )?.page;
    } else {
      page = rewrittenPage.react;
    }

    searchParams.set('schoolid', schoolid);
    searchParams.set('ssid', ssid);
    if (allSeasonId) searchParams.set('allSeasonId', allSeasonId as string);

    // in a csr link, the as is mapped to the brower address bar
    // and the href is mapped for next
    return {
      as: addGlobalQueryParams(asHref, appType),
      href: `${page}?${searchParams.toString()}`
    };
  }

  return { href: addGlobalQueryParams(href, appType) };
};

export const navigateToTeamPage = (
  teamContext: ITeamContext,
  appType: AppType,
  pageKey?: Page,
  params?: string | string[][] | Record<string, string> | URLSearchParams
) => {
  const { canonicalUrl, teamId, sportSeasonId, allSeasonId } = teamContext.data;
  const { scheduleModel: scheduleModelKey } =
    teamContext.sportData as never as {
      scheduleModel: keyof ScheduleModel;
    };

  const teamUrl = getTeamUrl(pageKey ?? Page.Home, canonicalUrl);

  const qs = params ? new URLSearchParams(params) : '';

  const fullUrl = qs ? `${teamUrl}?${qs}` : teamUrl;

  const anchorAttrs = getCSRRewriteAttributes(
    fullUrl,
    teamId,
    sportSeasonId,
    allSeasonId,
    appType,
    undefined,
    ScheduleModel[scheduleModelKey]
  );

  const { href, as } = anchorAttrs;

  routerPush(href, as);
};

export default getCSRRewriteAttributes;
